<template>
    <div class="faqs-list-with-search">
        <!-- Search Area -->
        <div v-if="showSearch" class="faq-header-area">
            <div class="faq-search">
                <div class="custom-search-bar" :class="{ 'searching': searching }">
                    <i v-if="!searching" class="fas fa-search search-icon"></i>
                    <i v-else class="fas fa-spinner fa-spin search-icon"></i>
                    <input
                        v-model="searchTerm"
                        type="text"
                        class="search-input"
                        :placeholder="searchPlaceholder"
                        @input="onSearchInput"
                        @keyup.enter="performSearch"
                        :disabled="searching"
                    />
                    <button
                        v-if="searchTerm && !searching"
                        class="clear-btn"
                        type="button"
                        @click="clearSearch"
                    >
                        <i class="fas fa-times"></i>
                    </button>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="text-center p-3">
            <div class="spinner-border spinner-border-sm" role="status">
                <span class="sr-only">Loading FAQs...</span>
            </div>
            <div class="mt-2 text-muted">
                {{ searchTerm ? 'Searching FAQs...' : 'Loading FAQs...' }}
            </div>
        </div>

        <!-- No FAQs State -->
        <div v-else-if="loaded && faqs.length === 0" class="text-center p-3 text-muted">
            <i class="fas fa-question-circle mb-2"></i>
            <div v-if="searchTerm">
                No FAQs found for "{{ searchTerm }}"
            </div>
            <div v-else>
                No FAQs available at the moment.
            </div>
            <div class="mt-2 empty-actions-container">
                <button
                    v-if="searchTerm"
                    type="button"
                    class="clear-search-btn"
                    @click="clearSearch"
                    title="Clear search"
                >
                    <i class="fas fa-times"></i>
                    <span>Clear Search</span>
                </button>
                <slot name="empty-actions" :open-messenger-contact="openMessengerContact"></slot>
            </div>
        </div>

        <!-- FAQ List -->
        <faqs-list
            v-else-if="faqs.length > 0"
            :faqs="faqs"
            :id-extension="idExtension"
        ></faqs-list>

        <!-- Footer slot for additional content like contact forms -->
        <slot name="footer"></slot>
    </div>
</template>

<script>
import FaqsList from './List.vue'

export default {
    name: 'FaqsListWithSearch',

    components: {
        FaqsList
    },

    props: {
        occasionId: {
            type: [Number, String],
            default: null
        },
        endpoint: {
            type: String,
            default: null
        },
        initialFaqs: {
            type: Array,
            default: () => []
        },
        showSearch: {
            type: Boolean,
            default: true
        },
        searchPlaceholder: {
            type: String,
            default: 'Search FAQs...'
        },
        idExtension: {
            type: String,
            default: 'default'
        },
        autoLoad: {
            type: Boolean,
            default: true
        },
        debounceDelay: {
            type: Number,
            default: 500
        }
    },

    data() {
        return {
            faqs: [...this.initialFaqs],
            searchTerm: '',
            loading: false,
            loaded: this.initialFaqs.length > 0,
            searching: false,
            searchTimeout: null
        }
    },

    computed: {
        computedEndpoint() {
            let endpoint = this.endpoint

            // If no endpoint provided, build from occasionId
            if (!endpoint && this.occasionId) {
                endpoint = `/api/v0/faqs/occasion/${this.occasionId}`
            }

            // If still no endpoint, use default
            if (!endpoint) {
                endpoint = '/api/v0/faqs'
            }

            // Add search parameter if search term exists
            if (this.searchTerm && this.searchTerm.trim() !== '') {
                const params = new URLSearchParams()
                params.append('question_or_answer', this.searchTerm.trim())
                endpoint += `?${params.toString()}`
            }

            return endpoint
        }
    },

    watch: {
        initialFaqs: {
            handler(newFaqs) {
                if (newFaqs && newFaqs.length > 0) {
                    this.faqs = [...newFaqs]
                    this.loaded = true
                }
            },
            deep: true
        }
    },

    mounted() {
        // Auto-load FAQs if no initial FAQs provided and autoLoad is true
        if (this.autoLoad && this.initialFaqs.length === 0) {
            this.loadFaqs()
        }
    },

    beforeDestroy() {
        // Clear any pending search timeout
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout)
        }
    },

    methods: {
        async loadFaqs(isSearch = false) {
            // Prevent multiple simultaneous requests
            if (this.loading || this.searching) return

            // Use different loading states for initial load vs search
            if (isSearch) {
                this.searching = true
            } else {
                this.loading = true
            }

            try {
                const response = await fetch(this.computedEndpoint)
                if (response.ok) {
                    // Check content type to ensure we're getting JSON
                    const contentType = response.headers.get('content-type')
                    if (!contentType || !contentType.includes('application/json')) {
                        console.error('Expected JSON but received:', contentType)
                        this.faqs = []
                        return
                    }

                    const responseData = await response.json()
                    // Handle the nested response structure: { status: "success", data: { faqs: [...] } }
                    if (responseData.status === 'success' && responseData.data && responseData.data.faqs) {
                        this.faqs = responseData.data.faqs
                    } else {
                        // Fallback for different response structures
                        this.faqs = responseData.faqs || responseData.data || responseData || []
                    }
                } else {
                    console.warn('Failed to load FAQs:', response.status, response.statusText)
                    this.faqs = []
                }
            } catch (error) {
                console.error('Error loading FAQs:', error)
                // If it's a JSON parse error, the API might have returned HTML
                if (error instanceof SyntaxError) {
                    console.error('API returned invalid JSON, possibly an error page. Check endpoint:', this.computedEndpoint)
                }
                this.faqs = []
            } finally {
                this.loaded = true
                this.loading = false
                this.searching = false
            }
        },

        performSearch() {
            this.loadFaqs(true)
        },

        clearSearch() {
            this.searchTerm = ''
            // Clear any pending debounced searches
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout)
                this.searchTimeout = null
            }

            // If we have initial FAQs, revert to them instead of making an API call
            if (this.initialFaqs.length > 0) {
                this.faqs = [...this.initialFaqs]
            } else {
                this.loadFaqs(false)
            }
        },

        onSearchInput() {
            // Clear existing timeout
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout)
                this.searchTimeout = null
            }

            // If search term is empty, revert to initial FAQs or load all
            if (!this.searchTerm || this.searchTerm.trim() === '') {
                // If we have initial FAQs, revert to them instead of making an API call
                if (this.initialFaqs.length > 0) {
                    this.faqs = [...this.initialFaqs]
                } else {
                    this.loadFaqs(false)
                }
                return
            }

            // Set new timeout for debounced search
            this.searchTimeout = setTimeout(() => {
                this.performSearch()
            }, this.debounceDelay)
        },

        // Public method to refresh FAQs (can be called from parent)
        refresh() {
            this.searchTerm = ''
            this.loadFaqs(false)
        },

        // Helper method to open messenger modal (if available)
        openMessengerContact() {
            if (window.messengerContainer) {
                window.messengerContainer.openModal('contact')
            }
        }
    }
}
</script>

<style scoped>
.faqs-list-with-search {
    display: flex;
    flex-direction: column;
}

/* FAQ Header Area Styling */
.faq-header-area {
    min-height: 20px;
    margin-bottom: 16px;
}

/* Custom seamless search bar */
.custom-search-bar {
    position: relative;
    display: flex;
    align-items: center;
    background-color: #f8f9fa;
    border: 1.5px solid #e0e0e0;
    border-radius: 20px;
    padding: 0.4rem 0.75rem;
    transition: all 0.2s ease;
    max-width: 100%;
}

.custom-search-bar:focus-within {
    border-color: #007bff;
    background-color: white;
    box-shadow: 0 0 0 0.15rem rgba(0, 123, 255, 0.1);
}

.custom-search-bar.searching {
    opacity: 0.7;
    pointer-events: none;
}

.search-icon {
    color: #999;
    font-size: 0.8rem;
    margin-right: 0.5rem;
    flex-shrink: 0;
}

.search-input {
    flex: 1;
    border: none;
    background: transparent;
    outline: none;
    font-size: 0.85rem;
}

.search-input::placeholder {
    color: #999;
    font-size: 0.8rem;
}

.clear-btn {
    background: none;
    border: none;
    color: #999;
    cursor: pointer;
    padding: 0.25rem;
    margin-left: 0.5rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    transition: all 0.2s ease;
}

.clear-btn:hover {
    background-color: #007bff;
    color: white;
}

.clear-btn:focus {
    outline: 2px solid #007bff;
    outline-offset: 1px;
}

.clear-btn i {
    font-size: 0.7rem;
}

/* Empty Actions Container */
.empty-actions-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
}

/* Clear Search Button (matches CustomerServiceAgent clear history style) */
.clear-search-btn {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    background: transparent;
    border: none;
    color: #9ca3af;
    padding: 0.25rem 0.5rem;
    border-radius: 12px;
    font-size: 0.75rem;
    cursor: pointer;
    transition: all 0.2s ease;
}

.clear-search-btn:hover {
    background: rgba(220, 53, 69, 0.1);
    color: #dc3545;
}

.clear-search-btn:active {
    transform: scale(0.98);
}

.clear-search-btn:focus {
    outline: 2px solid rgba(220, 53, 69, 0.3);
    outline-offset: 2px;
}

.clear-search-btn i {
    font-size: 0.7rem;
}

.clear-search-btn span {
    font-weight: 400;
}
</style>


// WEBPACK FOOTER //
// src/Assets/components/Faqs/ListWithSearch.vue